import React, { useCallback, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  getEntityFieldsWithBase,
  structDocumentationData,
} from "../../api/generated/StructDocs";
import { CRUDViewMode } from "../../types";
import EnumManager from "../../utils/enumManager";
import { UserCreateForm } from "../entities/user";
import "./CRUDPage.css";
import { DynamicForm } from "./DynamicForm";
import { EntityImageThumbnail } from "./EntityImageThumbnail";

// ソートの状態を表す型
type SortDirection = "asc" | "desc" | null;
interface SortState {
  field: string;
  direction: SortDirection;
}

// ページング設定
const DEFAULT_PAGE_SIZE = 100;

// カスタムフィールドレンダラーの引数型（DynamicFormからre-export）
interface CustomFieldRendererProps {
  value: any;
  onChange: (value: any) => void;
  disabled?: boolean;
  fieldName: string;
}

interface CRUDPageProps {
  entityName: string;
  entityDisplayName: string;
  userId?: string;
  mode: CRUDViewMode;
  data?: any[];
  item?: any;
  basePath?: string; // カスタムベースパス
  onDetail?: (id: string) => void; // 詳細ボタン
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => void;
  onCreate?: () => void;
  onSave?: (data: any) => void;
  onBack?: () => void; // 一覧に戻るボタン
  searchUI?: React.ReactNode; // 検索UI
  // ページングコールバック（オプション - サーバーサイドページングの場合に使用）
  onPageChange?: (offset: number, limit: number) => void;
  totalCount?: number; // サーバーサイドページングの場合の総件数
  // カスタム設定
  defaultFieldsWhenNoMetadata?: Array<{
    protoName: string;
    name: string;
    type: string;
    comment: string;
  }>; // メタデータがない場合のデフォルトフィールド
  defaultVisibleColumns?: string[]; // デフォルトで表示するカラムのリスト
  hideDefaultSearchField?: boolean; // デフォルトの検索フィールドを非表示にするかどうか
  renderBeforeField?: Record<string, () => React.ReactNode>; // 特定フィールドの前にカスタムコンテンツを挿入
  readOnlyFields?: string[]; // 読み取り専用フィールド（protoName）
  excludeFields?: string[]; // 除外フィールド（protoName）- フォームに表示しない
  customFieldRenderers?: Record<
    string,
    (props: CustomFieldRendererProps) => React.ReactNode
  >; // フィールドをカスタムコンポーネントに置換
  renderConfirmSummary?: (formData: Record<string, any>) => React.ReactNode; // 確認ダイアログのカスタム要約
  customColumnRenderers?: Record<
    string,
    (value: any, item: any) => React.ReactNode
  >; // 一覧表示用のカスタムカラムレンダラー
  customDetailRenderers?: Record<
    string,
    (value: any, item: any) => React.ReactNode
  >; // 詳細表示用のカスタムレンダラー
}

export const CRUDPage: React.FC<CRUDPageProps> = ({
  entityName,
  entityDisplayName,
  userId,
  mode,
  data = [],
  item,
  basePath,
  onDetail,
  onEdit,
  onDelete,
  onCreate,
  onSave,
  onBack,
  searchUI,
  onPageChange,
  totalCount,
  defaultFieldsWhenNoMetadata,
  defaultVisibleColumns,
  hideDefaultSearchField = false,
  renderBeforeField,
  readOnlyFields = [],
  excludeFields = [],
  customFieldRenderers = {},
  renderConfirmSummary,
  customColumnRenderers = {},
  customDetailRenderers = {},
}) => {
  const { id } = useParams();
  const navigate = useNavigate();

  // ソート状態
  const [sortState, setSortState] = useState<SortState>({
    field: "",
    direction: null,
  });

  // ページング状態
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(DEFAULT_PAGE_SIZE);

  // カラム表示選択状態
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  const [isColumnSelectorExpanded, setIsColumnSelectorExpanded] =
    useState(false);
  const [visibleColumns, setVisibleColumns] = useState<Set<string>>(new Set());

  // 検索クエリ
  const [searchQuery, setSearchQuery] = useState("");

  // エンティティ名を正規化（snake_case -> PascalCase、複数形 -> 単数形）
  const normalizeEntityName = (name: string) => {
    // 複数形の "s" を除去（例: "users" -> "user", "devices" -> "device"）
    let singularName = name;
    if (name.endsWith("s") && !name.endsWith("ss")) {
      singularName = name.slice(0, -1);
    }

    return singularName
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join("");
  };

  const normalizedEntityName = normalizeEntityName(entityName);
  // StructDocs.tsからエンティティメタデータを取得
  // 完全一致を優先し、部分一致はフォールバックとして使用
  const entityMetadata = useMemo(() => {
    // 1. typeNameの完全一致を最優先で探す
    const exactMatch = structDocumentationData.find(
      (struct) => struct.typeName === normalizedEntityName
    );
    if (exactMatch) return exactMatch;

    // 2. isEntity: true のエンティティから部分一致を探す
    const entityMatch = structDocumentationData.find(
      (struct) =>
        struct.isEntity &&
        (struct.displayName.toLowerCase().includes(entityName.toLowerCase()) ||
          struct.jpName.toLowerCase().includes(entityName.toLowerCase()))
    );
    if (entityMatch) return entityMatch;

    // 3. フォールバック：任意の構造体から部分一致
    return structDocumentationData.find(
      (struct) =>
        struct.displayName.toLowerCase().includes(entityName.toLowerCase()) ||
        struct.jpName.toLowerCase().includes(entityName.toLowerCase())
    );
  }, [entityName, normalizedEntityName]);

  // エンティティの基本フィールド（Goの埋め込み構造体から継承されるフィールド）
  const baseEntityFields = [
    { protoName: "id", name: "ID", type: "string", comment: "エンティティID" },
    {
      protoName: "createDate",
      name: "CreateDate",
      type: "Date",
      comment: "作成日時",
    },
    {
      protoName: "updateDate",
      name: "UpdateDate",
      type: "Date",
      comment: "更新日時",
    },
    {
      protoName: "version",
      name: "Version",
      type: "string",
      comment: "バージョン",
    },
  ];

  // 一覧表示用のフィールドを取得（全フィールド）
  const getAllFields = useCallback(() => {
    if (!entityMetadata) {
      // メタデータがない場合はデフォルトフィールドを返す
      if (defaultFieldsWhenNoMetadata) {
        return defaultFieldsWhenNoMetadata;
      }
      return [
        { protoName: "id", name: "ID", type: "string", comment: "" },
        { protoName: "name", name: "名前", type: "string", comment: "" },
        {
          protoName: "createDate",
          name: "作成日時",
          type: "Date",
          comment: "",
        },
        {
          protoName: "updateDate",
          name: "更新日時",
          type: "Date",
          comment: "",
        },
      ];
    }

    // メタデータのフィールドをフィルタリング
    const metadataFields = entityMetadata.fields.filter((field) => {
      // UserIDは表示しない（ルーティングで分かるため）
      if (field.protoName === "userId") return false;
      return true;
    });

    // 基本フィールド（id）を先頭に、メタデータフィールド、そして残りの基本フィールド（createDate, updateDate, version）を結合
    const idField = baseEntityFields.find((f) => f.protoName === "id")!;
    const timestampFields = baseEntityFields.filter(
      (f) => f.protoName !== "id"
    );

    // メタデータに含まれていない基本フィールドのみ追加
    const existingProtoNames = new Set(metadataFields.map((f) => f.protoName));
    const filteredTimestampFields = timestampFields.filter(
      (f) => !existingProtoNames.has(f.protoName)
    );

    return [idField, ...metadataFields, ...filteredTimestampFields];
  }, [entityMetadata, baseEntityFields, defaultFieldsWhenNoMetadata]);

  const allFields = getAllFields();

  // 表示するカラムを初期化（初回のみ）
  React.useEffect(() => {
    if (visibleColumns.size === 0 && allFields.length > 0) {
      if (defaultVisibleColumns) {
        // カスタムデフォルトカラムが指定されている場合
        const defaultFieldSet = new Set(defaultVisibleColumns);
        // OpenUserIDフィールドを検索（camelCaseまたはsnake_case）
        const openUserIDField = allFields.find(
          (f) =>
            f.protoName === "openUserID" ||
            f.protoName === "open_user_id" ||
            f.name === "公開ID" ||
            f.name === "OpenUserID"
        );
        // defaultVisibleColumnsにopenUserID/open_user_idが含まれている場合、実際のフィールド名に置き換え
        const resolvedColumns = defaultVisibleColumns.map((col) => {
          if (
            (col === "openUserID" || col === "open_user_id") &&
            openUserIDField
          ) {
            return openUserIDField.protoName;
          }
          return col;
        });
        setVisibleColumns(
          new Set(
            allFields
              .filter((f) => resolvedColumns.includes(f.protoName))
              .map((f) => f.protoName)
          )
        );
      } else {
        // その他のエンティティは全フィールドを表示
        setVisibleColumns(new Set(allFields.map((f) => f.protoName)));
      }
    }
  }, [allFields, visibleColumns.size, defaultVisibleColumns]);

  // defaultVisibleColumnsの順序でソートされたallFields
  const sortedAllFields = useMemo(() => {
    if (defaultVisibleColumns && defaultVisibleColumns.length > 0) {
      const orderMap = new Map(
        defaultVisibleColumns.map((col, index) => [col, index])
      );
      return [...allFields].sort((a, b) => {
        const orderA = orderMap.get(a.protoName) ?? 9999;
        const orderB = orderMap.get(b.protoName) ?? 9999;
        return orderA - orderB;
      });
    }
    return allFields;
  }, [allFields, defaultVisibleColumns]);

  // 表示中のフィールドのみフィルタリング（defaultVisibleColumnsの順序を維持）
  const listFields = useMemo(() => {
    if (visibleColumns.size === 0) return sortedAllFields;
    return sortedAllFields.filter((f) => visibleColumns.has(f.protoName));
  }, [sortedAllFields, visibleColumns]);

  // enum型かどうかを判定
  const isEnumType = (type: string): boolean => {
    return type.startsWith("consts.") || type.startsWith("Consts");
  };

  // Image型かどうかを判定（型名またはオブジェクトの構造で判定）
  const isImageType = (type: string): boolean => {
    return type.includes("Image") || type.includes("image");
  };

  // オブジェクトがImage情報を持つかどうかを判定
  const hasImageData = (value: any): boolean => {
    if (!value || typeof value !== "object") return false;
    // storagePath または storage_path プロパティを持つ場合はImage型と判定
    return "storagePath" in value || "storage_path" in value;
  };

  // Image情報からstoragePathを取得
  const getStoragePath = (value: any): string | undefined => {
    if (!value || typeof value !== "object") return undefined;
    return value.storagePath || value.storage_path;
  };

  // Image型フィールドをレンダリング（JSON + 画像表示）
  const renderImageField = (value: any, fieldName: string): React.ReactNode => {
    if (!value) return "-";

    // 配列の場合（contentImagesなど）
    if (Array.isArray(value)) {
      if (value.length === 0) return "-";
      return (
        <div className="image-field-container">
          <div className="image-field-json">
            <pre>{JSON.stringify(value, null, 2)}</pre>
          </div>
          <div className="image-field-gallery">
            {value.map((img, index) => {
              const storagePath = getStoragePath(img);
              return (
                <div key={index} className="image-field-item">
                  <EntityImageThumbnail
                    storagePath={storagePath}
                    alt={img.name || `${fieldName}-${index}`}
                    size={80}
                  />
                  {img.name && (
                    <span className="image-field-name">{img.name}</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      );
    }

    // 単一オブジェクトの場合（titleImageなど）
    const storagePath = getStoragePath(value);
    return (
      <div className="image-field-container">
        <div className="image-field-json">
          <pre>{JSON.stringify(value, null, 2)}</pre>
        </div>
        {storagePath && (
          <div className="image-field-preview">
            <EntityImageThumbnail
              storagePath={storagePath}
              alt={value.name || fieldName}
              size={120}
            />
          </div>
        )}
      </div>
    );
  };

  // enum値から「値: Comment名」形式のラベルを取得する関数
  const getEnumDisplayLabel = (
    enumType: string,
    value: number | string
  ): string => {
    // enumオブジェクトを取得
    const enumObject = EnumManager.getEnumObject(enumType);
    if (!enumObject) {
      return String(value);
    }

    // 値からキー（enum名）を逆引き
    let enumName = "";
    const numValue = typeof value === "number" ? value : parseInt(value, 10);
    for (const [key, enumValue] of Object.entries(enumObject)) {
      if (enumValue === numValue) {
        enumName = key;
        break;
      }
    }

    if (!enumName) {
      return String(value);
    }

    // ラベルフォーマッターを取得して「値: Comment名」形式で返す
    const labelFormatter = EnumManager.getEnumLabelFormatter(enumType);
    return labelFormatter(numValue, enumName);
  };

  // Timestamp型かどうかを判定
  const isTimestampValue = (value: any): boolean => {
    if (!value || typeof value !== "object") return false;
    return "seconds" in value || "nanos" in value;
  };

  // 時刻型フィールドかどうかを判定
  const isDateField = (fieldName?: string, fieldType?: string): boolean => {
    if (!fieldName) return false;
    const dateFieldNames = [
      "createDate",
      "updateDate",
      "beginDate",
      "endDate",
      "lastAuthDate",
      "masterSetDate",
      "create_date",
      "update_date",
      "begin_date",
      "end_date",
      "last_auth_date",
      "master_set_date",
    ];
    // フィールド名が日付フィールドのリストに含まれる場合、または
    // フィールド名が "Date" で終わる場合（camelCase）、または
    // フィールド名が "_date" で終わる場合（snake_case）
    const isDateFieldName = 
      dateFieldNames.includes(fieldName) ||
      fieldName.endsWith("Date") ||
      fieldName.endsWith("_date");
    return (
      isDateFieldName ||
      fieldType === "Date" ||
      fieldType === "google.protobuf.Timestamp"
    );
  };

  // Timestamp/Date値を日本時間でフォーマット
  const formatDateTimeJST = (value: any): string => {
    if (!value) return "-";
    try {
      let date: Date;
      if (typeof value === "object" && value.seconds !== undefined) {
        // Timestamp型の場合
        const seconds =
          typeof value.seconds === "bigint"
            ? Number(value.seconds)
            : parseInt(String(value.seconds || "0"));
        date = new Date(seconds * 1000);
      } else if (typeof value === "string" || typeof value === "number") {
        date = new Date(value);
      } else {
        return String(value);
      }
      if (isNaN(date.getTime())) return String(value);
      const weekdays = ["日", "月", "火", "水", "木", "金", "土"];
      const jstDate = new Date(date.getTime() + 9 * 60 * 60 * 1000);
      const year = jstDate.getUTCFullYear();
      const month = String(jstDate.getUTCMonth() + 1).padStart(2, "0");
      const day = String(jstDate.getUTCDate()).padStart(2, "0");
      const weekday = weekdays[jstDate.getUTCDay()];
      const hours = String(jstDate.getUTCHours()).padStart(2, "0");
      const minutes = String(jstDate.getUTCMinutes()).padStart(2, "0");
      return `${year}/${month}/${day}(${weekday}) ${hours}:${minutes}`;
    } catch {
      return String(value);
    }
  };

  // 報酬型かどうかを判定
  const isRewardField = (fieldName?: string): boolean => {
    return fieldName === "reward" || fieldName === "rewards";
  };

  // 報酬の要約を表示
  const formatRewardSummary = (reward: any): React.ReactNode => {
    if (!reward) return <span className="text-muted">-</span>;

    const items: string[] = [];
    if (reward.items?.length > 0) {
      items.push(`アイテム×${reward.items.length}`);
    }
    if (reward.characters?.length > 0) {
      items.push(`キャラ×${reward.characters.length}`);
    }
    if (reward.ornaments?.length > 0) {
      items.push(`装飾×${reward.ornaments.length}`);
    }
    if (reward.achievements?.length > 0) {
      items.push(`実績×${reward.achievements.length}`);
    }

    if (items.length === 0) {
      return <span className="text-muted">報酬なし</span>;
    }

    return <span className="reward-summary">{items.join(", ")}</span>;
  };

  // 報酬の詳細を表示
  const formatRewardDetail = (reward: any): React.ReactNode => {
    if (!reward) return <span className="text-muted">報酬なし</span>;

    const sections: React.ReactNode[] = [];

    if (reward.items?.length > 0) {
      sections.push(
        <div key="items" className="reward-detail-section">
          <div className="reward-detail-title">🎁 アイテム報酬</div>
          <div className="reward-detail-list">
            {reward.items.map((item: any, idx: number) => (
              <div key={idx} className="reward-detail-item">
                <span>
                  アイテムID: {item.mstItemId || item.mst_item_id || "-"}
                </span>
                <span className="reward-amount">
                  ×{item.acquiredAmount || item.acquired_amount || 1}
                </span>
              </div>
            ))}
          </div>
        </div>
      );
    }

    if (reward.characters?.length > 0) {
      sections.push(
        <div key="characters" className="reward-detail-section">
          <div className="reward-detail-title">👤 キャラクター報酬</div>
          <div className="reward-detail-list">
            {reward.characters.map((char: any, idx: number) => (
              <div key={idx} className="reward-detail-item">
                <span>
                  キャラID:{" "}
                  {char.mstCharacterId || char.mst_character_id || "-"}
                </span>
              </div>
            ))}
          </div>
        </div>
      );
    }

    if (reward.ornaments?.length > 0) {
      sections.push(
        <div key="ornaments" className="reward-detail-section">
          <div className="reward-detail-title">✨ オーナメント報酬</div>
          <div className="reward-detail-list">
            {reward.ornaments.map((orn: any, idx: number) => (
              <div key={idx} className="reward-detail-item">
                <span>
                  オーナメントID:{" "}
                  {orn.mstOrnamentId || orn.mst_ornament_id || "-"}
                </span>
              </div>
            ))}
          </div>
        </div>
      );
    }

    if (reward.achievements?.length > 0) {
      sections.push(
        <div key="achievements" className="reward-detail-section">
          <div className="reward-detail-title">🏆 実績報酬</div>
          <div className="reward-detail-list">
            {reward.achievements.map((ach: any, idx: number) => (
              <div key={idx} className="reward-detail-item">
                <span>
                  実績ID:{" "}
                  {ach.mstAchievementId || ach.mst_achievement_id || "-"}
                </span>
              </div>
            ))}
          </div>
        </div>
      );
    }

    if (sections.length === 0) {
      return <span className="text-muted">報酬なし</span>;
    }

    return <div className="reward-detail-container">{sections}</div>;
  };

  // フィールド値をフォーマット
  const formatFieldValue = (
    value: any,
    fieldType: string,
    fieldName?: string
  ) => {
    // bool型の場合は null/undefined を false として扱い、チェックボックス風で表示
    if (fieldType === "boolean") {
      const boolValue = Boolean(value);
      return (
        <span
          className={`checkbox-display ${boolValue ? "checked" : "unchecked"}`}
        >
          {boolValue ? "✓" : "✗"}
        </span>
      );
    }

    if (value === null || value === undefined) return "-";

    // 時刻型フィールドの場合
    if (isDateField(fieldName, fieldType) || isTimestampValue(value)) {
      return formatDateTimeJST(value);
    }

    // 報酬型フィールドの場合（一覧表示用）
    if (isRewardField(fieldName) && typeof value === "object") {
      return formatRewardSummary(value);
    }

    // enum型フィールドの場合は「値: Comment名」形式で表示
    if (isEnumType(fieldType)) {
      return getEnumDisplayLabel(fieldType, value);
    }

    // Dateオブジェクトの場合は日本時間フォーマットで表示
    if (value instanceof Date) {
      return formatDateTimeJST(value);
    }

    switch (fieldType) {
      case "string":
        // IDカラムは省略しない、それ以外の長い文字列は省略
        const str = String(value);
        if (fieldName === "id") {
          return str;
        }
        return str.length > 30 ? str.substring(0, 30) + "..." : str;
      case "number":
        return String(value);
      case "Date":
        return formatDateTimeJST(value);
      default:
        return String(value);
    }
  };

  const getBasePath = () => {
    if (basePath) {
      return basePath;
    }
    if (userId) {
      return `/users/${userId}/entity/${entityName}`;
    }
    return `/entity/${entityName}`;
  };

  const handleDetail = (itemId: string) => {
    if (onDetail) {
      onDetail(itemId);
    } else {
      navigate(`${getBasePath()}/${itemId}`);
    }
  };

  const handleEdit = (itemId: string) => {
    if (onEdit) {
      onEdit(itemId);
    } else {
      navigate(`${getBasePath()}/${itemId}/edit`);
    }
  };

  const handleDelete = async (itemId: string) => {
    if (onDelete && window.confirm(`${entityDisplayName}を削除しますか？`)) {
      try {
        onDelete(itemId);
        // 詳細ページから削除した場合は一覧ページに移動
        if (mode === "detail") {
          navigate(getBasePath());
        }
      } catch (error) {
        console.error("削除に失敗しました:", error);
        // エラーハンドリングは呼び出し元で行われることを想定
      }
    }
  };

  const handleCreate = () => {
    if (onCreate) {
      onCreate();
    } else {
      navigate(`${getBasePath()}/create`);
    }
  };

  // データから空のオブジェクトや無効なエンティティを除外
  // entityNameから動的にプロパティ名を推測（例: "user_profile" -> "userProfiles"）
  const getEntityPropertyName = (entityName: string) => {
    // snake_caseをcamelCaseに変換し、複数形にする
    const camelCase = entityName
      .split("_")
      .map((word, index) =>
        index === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1)
      )
      .join("");

    // 複数形に変換（sを付けるだけ）
    return camelCase + "s";
  };

  // dataから対応するプロパティを取得
  const propertyName = getEntityPropertyName(entityName);
  let entityData: any[] = [];

  if (Array.isArray(data)) {
    if (data.length > 0 && data[0][propertyName]) {
      // UserID関連エンティティの場合：data[0][propertyName]からデータを取得
      entityData = data[0][propertyName];
    } else {
      // 非UserID関連エンティティの場合：dataが直接配列
      entityData = data;
    }
  }

  const validData = Array.isArray(entityData)
    ? entityData.filter(
        (item) =>
          item &&
          typeof item === "object" &&
          Object.keys(item).length > 0 &&
          item.id !== undefined &&
          item.id !== null &&
          item.id !== ""
      )
    : [];

  // ソート関数
  const sortData = useCallback((data: any[], sort: SortState) => {
    if (!sort.field || !sort.direction) return data;

    return [...data].sort((a, b) => {
      const aVal = a[sort.field];
      const bVal = b[sort.field];

      // null/undefined の処理
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return sort.direction === "asc" ? 1 : -1;
      if (bVal == null) return sort.direction === "asc" ? -1 : 1;

      // 日付型の比較
      if (
        aVal instanceof Date ||
        (typeof aVal === "string" && !isNaN(Date.parse(aVal)))
      ) {
        const aDate = new Date(aVal).getTime();
        const bDate = new Date(bVal).getTime();
        return sort.direction === "asc" ? aDate - bDate : bDate - aDate;
      }

      // 数値型の比較
      if (typeof aVal === "number" && typeof bVal === "number") {
        return sort.direction === "asc" ? aVal - bVal : bVal - aVal;
      }

      // 文字列比較
      const aStr = String(aVal).toLowerCase();
      const bStr = String(bVal).toLowerCase();
      if (aStr < bStr) return sort.direction === "asc" ? -1 : 1;
      if (aStr > bStr) return sort.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, []);

  // 検索フィルタリングされたデータ
  const filteredData = useMemo(() => {
    if (!searchQuery.trim()) return validData;

    const query = searchQuery.toLowerCase().trim();
    return validData.filter((item) => {
      // 全てのフィールドの値を検索対象にする
      return Object.values(item).some((value) => {
        if (value === null || value === undefined) return false;
        const strValue = String(value).toLowerCase();
        return strValue.includes(query);
      });
    });
  }, [validData, searchQuery]);

  // ソートされたデータ
  const sortedData = useMemo(() => {
    return sortData(filteredData, sortState);
  }, [filteredData, sortState, sortData]);

  // ページングされたデータ（クライアントサイドページング）
  const pagedData = useMemo(() => {
    // サーバーサイドページングの場合はそのまま返す
    if (onPageChange) return sortedData;

    const start = currentPage * pageSize;
    return sortedData.slice(start, start + pageSize);
  }, [sortedData, currentPage, pageSize, onPageChange]);

  // 総ページ数
  const totalPages = useMemo(() => {
    const total = totalCount ?? sortedData.length;
    return Math.ceil(total / pageSize);
  }, [totalCount, sortedData.length, pageSize]);

  // ソートハンドラー
  const handleSort = (fieldName: string) => {
    setSortState((prev) => {
      if (prev.field !== fieldName) {
        return { field: fieldName, direction: "asc" };
      }
      if (prev.direction === "asc") {
        return { field: fieldName, direction: "desc" };
      }
      return { field: "", direction: null };
    });
    setCurrentPage(0); // ソート変更時はページを先頭に戻す
  };

  // ページ変更ハンドラー
  const handlePageChange = (newPage: number) => {
    if (newPage < 0 || newPage >= totalPages) return;
    setCurrentPage(newPage);

    // サーバーサイドページングの場合
    if (onPageChange) {
      onPageChange(newPage * pageSize, pageSize);
    }
  };

  // カラム表示切り替え
  const toggleColumn = (protoName: string) => {
    setVisibleColumns((prev) => {
      const next = new Set(prev);
      if (next.has(protoName)) {
        // 最低1つは残す
        if (next.size > 1) {
          next.delete(protoName);
        }
      } else {
        next.add(protoName);
      }
      return next;
    });
  };

  // 全カラム表示/非表示
  const toggleAllColumns = (show: boolean) => {
    if (show) {
      setVisibleColumns(new Set(allFields.map((f) => f.protoName)));
    } else {
      // 最低IDは残す
      setVisibleColumns(new Set(["id"]));
    }
  };

  // ソートアイコンを取得
  const getSortIcon = (fieldName: string) => {
    if (sortState.field !== fieldName) return "↕";
    if (sortState.direction === "asc") return "↑";
    if (sortState.direction === "desc") return "↓";
    return "↕";
  };

  const renderListView = () => (
    <div className="crud-page">
      <div className="crud-header">
        <h1>{entityDisplayName}一覧</h1>
        <div className="crud-actions">
          {onCreate && (
            <button className="btn btn-primary" onClick={handleCreate}>
              新規作成
            </button>
          )}
        </div>
      </div>

      {/* カラム選択パネル（折りたたみ可能） */}
      <div
        className={`column-selector-panel ${
          isColumnSelectorExpanded ? "expanded" : ""
        }`}
      >
        <div className="column-selector-header">
          <button
            className="column-selector-toggle"
            onClick={() =>
              setIsColumnSelectorExpanded(!isColumnSelectorExpanded)
            }
          >
            <span>表示カラムを選択</span>
            <span
              className={`column-selector-arrow ${
                isColumnSelectorExpanded ? "expanded" : ""
              }`}
            >
              ▶
            </span>
          </button>
          {isColumnSelectorExpanded && (
            <div className="column-selector-actions">
              <button
                className="btn btn-sm btn-secondary"
                onClick={() => toggleAllColumns(true)}
              >
                全選択
              </button>
              <button
                className="btn btn-sm btn-secondary"
                onClick={() => toggleAllColumns(false)}
              >
                全解除
              </button>
            </div>
          )}
        </div>
        {isColumnSelectorExpanded && (
          <div className="column-selector-list">
            {sortedAllFields.map((field) => (
              <label key={field.protoName} className="column-checkbox">
                <input
                  type="checkbox"
                  checked={visibleColumns.has(field.protoName)}
                  onChange={() => toggleColumn(field.protoName)}
                />
                <span className="column-checkbox-label">
                  <span className="column-jp-name">{field.name}</span>
                  {field.comment && (
                    <span className="column-comment">{field.comment}</span>
                  )}
                </span>
              </label>
            ))}
          </div>
        )}
      </div>

      {searchUI}

      {/* ページング情報 */}
      <div className="pagination-info">
        <span>
          {searchQuery.trim() ? (
            <>
              全 {validData.length} 件中{" "}
              <strong>{sortedData.length} 件が検索にヒット</strong>
              {sortedData.length > 0 &&
                ` → ${currentPage * pageSize + 1} - ${Math.min(
                  (currentPage + 1) * pageSize,
                  sortedData.length
                )} 件を表示`}
            </>
          ) : (
            <>
              全 {totalCount ?? sortedData.length} 件中{" "}
              {sortedData.length > 0
                ? `${currentPage * pageSize + 1} - ${Math.min(
                    (currentPage + 1) * pageSize,
                    totalCount ?? sortedData.length
                  )} 件を表示`
                : "0 件を表示"}
            </>
          )}
        </span>
      </div>

      {/* 検索フィールド */}
      {!hideDefaultSearchField && (
        <div className="search-field-container">
          <input
            type="text"
            className="search-input"
            placeholder="検索（全フィールドを対象）..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setCurrentPage(0); // 検索時はページを先頭に戻す
            }}
          />
          {searchQuery && (
            <button
              className="search-clear-btn"
              onClick={() => {
                setSearchQuery("");
                setCurrentPage(0);
              }}
            >
              ✕
            </button>
          )}
        </div>
      )}

      <div className="crud-content">
        {sortedData.length === 0 ? (
          <div className="empty-state">
            <p>{entityDisplayName}がありません</p>
            {onCreate && (
              <button className="btn btn-primary" onClick={handleCreate}>
                新規作成
              </button>
            )}
          </div>
        ) : (
          <div className="crud-table-container">
            <table className="crud-table">
              <thead>
                <tr>
                  {listFields.map(
                    (
                      field: {
                        protoName: string;
                        comment?: string;
                        name:
                          | string
                          | number
                          | boolean
                          | React.ReactElement<
                              any,
                              string | React.JSXElementConstructor<any>
                            >
                          | Iterable<React.ReactNode>
                          | React.ReactPortal;
                      },
                      index: number
                    ) => (
                      <th
                        key={`header-${field.protoName}-${index}`}
                        className="sortable-header"
                        onClick={() => handleSort(field.protoName)}
                      >
                        <div className="header-content">
                          <div className="header-names">
                            <span className="header-jp-name">{field.name}</span>
                            {field.comment && (
                              <span className="header-comment">
                                {field.comment}
                              </span>
                            )}
                          </div>
                          <span className="sort-icon">
                            {getSortIcon(field.protoName)}
                          </span>
                        </div>
                      </th>
                    )
                  )}
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {pagedData.map((item, itemIndex) => (
                  <tr key={item.id || `item-${itemIndex}`}>
                    {listFields.map((field, fieldIndex) => {
                      const fieldValue = item[field.protoName];
                      return (
                        <td
                          key={`${item.id || itemIndex}-${
                            field.protoName
                          }-${fieldIndex}`}
                        >
                          {field.protoName === "id" ? (
                            <Link
                              to={`${getBasePath()}/${item.id}`}
                              className="link"
                            >
                              {formatFieldValue(
                                fieldValue,
                                field.type,
                                field.protoName
                              )}
                            </Link>
                          ) : customColumnRenderers[field.protoName] ? (
                            customColumnRenderers[field.protoName](
                              fieldValue,
                              item
                            )
                          ) : (
                            formatFieldValue(
                              fieldValue,
                              field.type,
                              field.protoName
                            )
                          )}
                        </td>
                      );
                    })}
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => handleDetail(item.id)}
                        >
                          詳細
                        </button>
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => handleEdit(item.id)}
                        >
                          編集
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDelete(item.id)}
                        >
                          削除
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ページングコントロール */}
      {sortedData.length > 0 && (
        <div className="pagination-controls">
          <button
            className="btn btn-secondary"
            onClick={() => handlePageChange(0)}
            disabled={currentPage === 0}
          >
            ≪ 最初
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 0}
          >
            ← 前へ
          </button>
          <span className="pagination-current">
            {currentPage + 1} / {totalPages} ページ
          </span>
          <button
            className="btn btn-secondary"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage >= totalPages - 1}
          >
            次へ →
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => handlePageChange(totalPages - 1)}
            disabled={currentPage >= totalPages - 1}
          >
            最後 ≫
          </button>
        </div>
      )}
    </div>
  );

  const renderDetailView = () => (
    <div className="crud-page">
      <div className="crud-header">
        <h1>{entityDisplayName}詳細</h1>
        <div className="crud-actions">
          {onBack && (
            <button className="btn btn-outline" onClick={onBack}>
              一覧に戻る
            </button>
          )}
          <button className="btn btn-secondary" onClick={() => handleEdit(id!)}>
            編集
          </button>
          <button className="btn btn-danger" onClick={() => handleDelete(id!)}>
            削除
          </button>
        </div>
      </div>
      <div className="crud-content">
        {item ? (
          <div className="detail-view">
            {entityMetadata
              ? // エンティティメタデータがある場合は指定順で表示
                (() => {
                  const fields = getEntityFieldsWithBase(entityMetadata);

                  // フィールドを分類
                  const idFields: typeof fields = [];
                  const userIdFields: typeof fields = [];
                  const relatedIdFields: typeof fields = [];
                  const systemFields: typeof fields = [];
                  const otherFields: typeof fields = [];

                  fields.forEach((field) => {
                    if (field.protoName === "id") {
                      idFields.push(field);
                    } else if (field.protoName === "userId") {
                      userIdFields.push(field);
                    } else if (
                      field.protoName.endsWith("_id") ||
                      field.name.endsWith("ID") ||
                      field.name.endsWith("Id")
                    ) {
                      // IDに関連するフィールド
                      relatedIdFields.push(field);
                    } else if (
                      ["createDate", "updateDate", "version"].includes(
                        field.protoName
                      )
                    ) {
                      systemFields.push(field);
                    } else {
                      otherFields.push(field);
                    }
                  });

                  // システムフィールドを指定順にソート
                  const sortedSystemFields = systemFields.sort((a, b) => {
                    const order = ["createDate", "updateDate", "version"];
                    return (
                      order.indexOf(a.protoName) - order.indexOf(b.protoName)
                    );
                  });

                  // 最終的な順序: ID → UserID → 関連ID → その他 → システムフィールド
                  const sortedFields = [
                    ...idFields,
                    ...userIdFields,
                    ...relatedIdFields,
                    ...otherFields,
                    ...sortedSystemFields,
                  ];

                  return sortedFields.map((field) => {
                    const fieldValue = item[field.protoName];
                    const fieldIsImage =
                      isImageType(field.type) ||
                      hasImageData(fieldValue) ||
                      (Array.isArray(fieldValue) &&
                        fieldValue.length > 0 &&
                        hasImageData(fieldValue[0]));

                    // カスタム詳細レンダラーがある場合はそれを使用
                    const customRenderer =
                      customDetailRenderers[field.protoName];

                    // 値のレンダリングを決定
                    const renderValue = () => {
                      if (customRenderer) {
                        return customRenderer(fieldValue, item);
                      }
                      if (fieldIsImage) {
                        return renderImageField(fieldValue, field.name);
                      }
                      // 報酬型フィールドの場合
                      if (
                        isRewardField(field.protoName) &&
                        typeof fieldValue === "object"
                      ) {
                        return formatRewardDetail(fieldValue);
                      }
                      // 時刻型フィールドの場合
                      if (
                        isTimestampValue(fieldValue) ||
                        isDateField(field.protoName, field.type)
                      ) {
                        return formatDateTimeJST(fieldValue);
                      }
                      // その他のオブジェクト
                      if (
                        typeof fieldValue === "object" &&
                        fieldValue !== null
                      ) {
                        return (
                          <pre className="json-display">
                            {JSON.stringify(fieldValue, null, 2)}
                          </pre>
                        );
                      }
                      return formatFieldValue(
                        fieldValue,
                        field.type,
                        field.protoName
                      );
                    };

                    return (
                      <div key={field.protoName} className="detail-item">
                        <label>{field.name}:</label>
                        <div className="detail-value">{renderValue()}</div>
                      </div>
                    );
                  });
                })()
              : // メタデータがない場合は従来通り
                Object.entries(item).map(([fieldKey, value]) => {
                  const fieldIsImage =
                    hasImageData(value) ||
                    (Array.isArray(value) &&
                      value.length > 0 &&
                      hasImageData(value[0]));

                  // カスタム詳細レンダラーがある場合はそれを使用
                  const customRenderer = customDetailRenderers[fieldKey];

                  // 値のレンダリングを決定
                  const renderValue = () => {
                    if (customRenderer) {
                      return customRenderer(value, item);
                    }
                    if (fieldIsImage) {
                      return renderImageField(value, fieldKey);
                    }
                    // 報酬型フィールドの場合
                    if (isRewardField(fieldKey) && typeof value === "object") {
                      return formatRewardDetail(value);
                    }
                    // 時刻型フィールドの場合
                    if (isTimestampValue(value) || isDateField(fieldKey)) {
                      return formatDateTimeJST(value);
                    }
                    // その他のオブジェクト
                    if (typeof value === "object" && value !== null) {
                      return (
                        <pre className="json-display">
                          {JSON.stringify(value, null, 2)}
                        </pre>
                      );
                    }
                    return String(value);
                  };

                  return (
                    <div key={fieldKey} className="detail-item">
                      <label>{fieldKey}:</label>
                      <div className="detail-value">{renderValue()}</div>
                    </div>
                  );
                })}
          </div>
        ) : (
          <div className="empty-state">
            <p>データが見つかりません</p>
          </div>
        )}
      </div>
    </div>
  );

  const renderEditView = () => (
    <div className="crud-page">
      <div className="crud-header">
        <h1>{entityDisplayName}編集</h1>
        <div className="crud-actions">
          {onBack && (
            <button className="btn btn-outline" onClick={onBack}>
              一覧に戻る
            </button>
          )}
          <button className="btn btn-danger" onClick={() => handleDelete(id!)}>
            削除
          </button>
        </div>
      </div>
      <div className="crud-content">
        <DynamicForm
          entityName={entityName}
          mode="edit"
          initialData={item}
          userId={userId}
          renderBeforeField={renderBeforeField}
          readOnlyFields={readOnlyFields}
          excludeFields={excludeFields}
          customFieldRenderers={customFieldRenderers}
          delegateSubmit={!!onSave}
          renderConfirmSummary={renderConfirmSummary}
          onSubmit={(data) => {
            if (onSave) {
              onSave(data);
            } else {
              console.log("更新データ:", data);
              navigate(-1);
            }
          }}
          onCancel={() => navigate(-1)}
        />
      </div>
    </div>
  );

  const renderCreateView = () => (
    <div className="crud-page">
      <div className="crud-header">
        <h1>{entityDisplayName}新規作成</h1>
        <div className="crud-actions">
          {onBack && (
            <button className="btn btn-outline" onClick={onBack}>
              一覧に戻る
            </button>
          )}
        </div>
      </div>
      <div className="crud-content">
        {entityName === "users" ? (
          <UserCreateForm
            onSuccess={(user) => {
              console.log("ユーザー作成成功:", user);
              navigate("/users");
            }}
            onCancel={() => navigate("/users")}
          />
        ) : (
          <DynamicForm
            entityName={entityName}
            mode="create"
            userId={userId}
            renderBeforeField={renderBeforeField}
            readOnlyFields={readOnlyFields}
            excludeFields={excludeFields}
            customFieldRenderers={customFieldRenderers}
            delegateSubmit={!!onSave}
            renderConfirmSummary={renderConfirmSummary}
            onSubmit={(data) => {
              if (onSave) {
                onSave(data);
              } else {
                console.log("作成データ:", data);
                navigate(-1);
              }
            }}
            onCancel={() => navigate(-1)}
          />
        )}
      </div>
    </div>
  );

  switch (mode) {
    case "list":
      return renderListView();
    case "detail":
      return renderDetailView();
    case "edit":
      return renderEditView();
    case "create":
      return renderCreateView();
    default:
      return <div>不明なモード</div>;
  }
};
